import type { ElkNode } from 'elkjs/lib/elk.bundled';
import type { WorkflowReadOnlyGraph } from './workflow-read-only-graph.models';

export interface WorkflowMapPoint {
  readonly x: number;
  readonly y: number;
}
export type WorkflowMapPositions = Readonly<Record<string, WorkflowMapPoint>>;

/** Layout follows forward work; return edges remain visible but never push earlier work below the finish. */
export function workflowMapLayout(graph: WorkflowReadOnlyGraph): ElkNode {
  const nodes = graph.nodes,
    byId = new Map(nodes.map((n) => [n.id, n]));
  const order = new Map(nodes.map((n, i) => [n.id, i]));
  const compare = (a: string, b: string) =>
    (byId.get(a)?.displayOrder ?? order.get(a) ?? 0) -
      (byId.get(b)?.displayOrder ?? order.get(b) ?? 0) ||
    (order.get(a) ?? 0) - (order.get(b) ?? 0);
  const next = new Map(nodes.map((n) => [n.id, [] as string[]]));
  for (const edge of graph.edges)
    if (
      edge.pathKindCode !== 'return' &&
      edge.source !== edge.target &&
      byId.has(edge.source) &&
      byId.has(edge.target)
    ) {
      const targets = next.get(edge.source)!;
      if (!targets.includes(edge.target)) targets.push(edge.target);
    }
  for (const targets of next.values()) targets.sort(compare);
  // Ignore only DFS back-edges for arranging malformed/unmarked cycles; never change authored routes.
  const active = new Set<string>(),
    seen = new Set<string>(),
    dag = new Map(nodes.map((n) => [n.id, [] as string[]]));
  const visit = (id: string) => {
    if (seen.has(id)) return;
    seen.add(id);
    active.add(id);
    for (const target of next.get(id) ?? []) {
      if (active.has(target)) continue;
      visit(target);
      dag.get(id)!.push(target);
    }
    active.delete(id);
  };
  const starts = [
    ...nodes.filter((n) => n.kind === 'entry').map((n) => n.id),
    ...nodes.map((n) => n.id).sort(compare),
  ];
  for (const id of starts) visit(id);
  const length = new Map<string, number>();
  const distance = (id: string): number => {
    if (length.has(id)) return length.get(id)!;
    const value = 1 + Math.max(0, ...dag.get(id)!.map(distance));
    length.set(id, value);
    return value;
  };
  for (const node of nodes) distance(node.id);
  let head = starts[0];
  if (!nodes.some((n) => n.kind === 'entry'))
    head = [...nodes].sort(
      (a, b) => distance(b.id) - distance(a.id) || compare(a.id, b.id),
    )[0]?.id;
  const spine = new Set<string>();
  while (head) {
    spine.add(head);
    head = [...dag.get(head)!].sort(
      (a, b) => distance(b) - distance(a) || compare(a, b),
    )[0];
  }
  const incoming = new Map(nodes.map((n) => [n.id, 0]));
  for (const targets of dag.values())
    for (const id of targets) incoming.set(id, incoming.get(id)! + 1);
  const queue = nodes
      .map((n) => n.id)
      .filter((id) => incoming.get(id) === 0)
      .sort(compare),
    rank = new Map(nodes.map((n) => [n.id, 0]));
  for (let i = 0; i < queue.length; i++) {
    const id = queue[i];
    for (const target of dag.get(id)!) {
      rank.set(target, Math.max(rank.get(target)!, rank.get(id)! + 1));
      incoming.set(target, incoming.get(target)! - 1);
      if (incoming.get(target) === 0) queue.push(target);
    }
  }
  const occupied = new Map<number, Set<number>>(),
    columns = new Map<string, number>();
  for (const id of [
    ...spine,
    ...nodes
      .map((n) => n.id)
      .filter((id) => !spine.has(id))
      .sort(compare),
  ]) {
    const row = rank.get(id)!,
      used = occupied.get(row) ?? new Set<number>();
    let column = 0;
    if (!spine.has(id)) {
      column = -1;
      while (used.has(column)) column = column < 0 ? -column : -column - 1;
    }
    used.add(column);
    occupied.set(row, used);
    columns.set(id, column);
  }
  const minimum = Math.min(0, ...columns.values());
  return {
    id: 'workflow-map',
    children: nodes.map((n) => ({
      id: n.id,
      x: (columns.get(n.id)! - minimum) * 432,
      y: rank.get(n.id)! * 80,
      width: 404,
      height: 60,
    })),
    width: (Math.max(0, ...columns.values()) - minimum) * 432 + 404,
    height: Math.max(0, ...rank.values()) * 80 + 60,
  };
}

const prefix = 'phms.workflow-map.v1:';
export function readWorkflowMapPositions(
  storage: Storage | null,
  key: string | null,
  ids: readonly string[],
): WorkflowMapPositions {
  if (!storage || !key) return {};
  try {
    const data = JSON.parse(storage.getItem(prefix + key) ?? 'null');
    if (
      !data ||
      data.version !== 1 ||
      !data.positions ||
      typeof data.positions !== 'object'
    )
      return {};
    const allowed = new Set(ids),
      positions: Record<string, WorkflowMapPoint> = {};
    for (const [id, p] of Object.entries(data.positions).slice(0, 1000))
      if (allowed.has(id) && validPoint(p)) positions[id] = { x: p.x, y: p.y };
    return positions;
  } catch {
    return {};
  }
}
export function validPoint(value: unknown): value is WorkflowMapPoint {
  const p = value as WorkflowMapPoint | null;
  return (
    !!p &&
    Number.isFinite(p.x) &&
    Number.isFinite(p.y) &&
    Math.abs(p.x) <= 100000 &&
    Math.abs(p.y) <= 100000
  );
}
export function saveWorkflowMapPositions(
  storage: Storage | null,
  key: string | null,
  positions: WorkflowMapPositions,
): boolean {
  if (!storage || !key) return false;
  try {
    if (Object.keys(positions).length)
      storage.setItem(prefix + key, JSON.stringify({ version: 1, positions }));
    else storage.removeItem(prefix + key);
    return true;
  } catch {
    return false;
  }
}
